/*  */

import React, { useEffect, useMemo, useRef } from 'react'
import { DateRangePicker } from '@blueprintjs/datetime'
import { injectIntl } from 'react-intl'
import { formatLocalDate } from './dateHelpers'

function DatePicker ({
  minDate: minDateProp,
  calendarMinDate: calendarMinDateProp,
  maxDate: maxDateProp,
  value,
  onRangeChange,
  className,
  intl,
}) {
  const minDate = minDateProp || new Date(2000, 0, 1)
  const calendarMinDate = calendarMinDateProp || minDate
  const maxDate = maxDateProp || new Date()
  const pickerRootRef = useRef(null)

  const end = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
  const startFromDays = d =>
    new Date(end.getFullYear(), end.getMonth(), end.getDate() - d + 1)

  const translatedShortcuts = useMemo(() => ([
    {
      label: intl.formatMessage({ id: 'cases.stats.show.dateRangeAllTime' }),
      dateRange: [minDate, end],
    },
    {
      label: intl.formatMessage({
        id: 'cases.stats.show.dateRangePast7Days',
      }),
      dateRange: [startFromDays(7), end],
    },
    {
      label: intl.formatMessage({
        id: 'cases.stats.show.dateRangePast30Days',
      }),
      dateRange: [startFromDays(30), end],
    },
    {
      label: intl.formatMessage({
        id: 'cases.stats.show.dateRangePastYear',
      }),
      dateRange: [startFromDays(365), end],
    },
    {
      label: intl.formatMessage({
        id: 'cases.stats.show.dateRangePast2Years',
      }),
      dateRange: [startFromDays(730), end],
    },
  ]), [intl, minDate, end])

  const getInitialMonth = (dateRange) => {
    if (!dateRange || !dateRange[0]) return undefined
    const fromDate = dateRange[0]
    const initialMonth = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1)

    // Clamp initialMonth to be within minDate and maxDate bounds
    const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1)
    const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)

    if (initialMonth < minMonth) return minMonth
    if (initialMonth > maxMonth) return maxMonth
    return initialMonth
  }

  const getSelectedShortcutIndex = (currentRange, shortcutsList) => {
    if (!currentRange || !shortcutsList || !Array.isArray(shortcutsList)) {
      return -1
    }

    const [currentFrom, currentTo] = currentRange
    const toDateStr = d => (d ? formatLocalDate(d) : '')

    for (let i = 0; i < shortcutsList.length; i++) {
      const [shortcutFrom, shortcutTo] = shortcutsList[i].dateRange
      if (
        toDateStr(shortcutFrom) === toDateStr(currentFrom) &&
        toDateStr(shortcutTo) === toDateStr(currentTo)
      ) {
        return i
      }
    }
    return -1
  }

  const selectedShortcutIndex = getSelectedShortcutIndex(value, translatedShortcuts)

  useEffect(() => {
    const root = pickerRootRef.current
    if (!root) return

    const shortcutItems = root.querySelectorAll(
      [
        '.bp6-daterangepicker-shortcuts .bp6-menu-item',
        '.bp6-daterangepicker-shortcuts .bp6-menu-item',
      ].join(', ')
    )

    for (let i = 0; i < shortcutItems.length; i++) {
      const item = shortcutItems[i]
      if (item && item.classList) {
        item.classList.toggle('bp6-active', i === selectedShortcutIndex)
        item.classList.toggle('bp6-active', i === selectedShortcutIndex)
      }
    }
  })

  function handleChange (nextRange) {
    if (onRangeChange) {
      onRangeChange(nextRange[0], nextRange[1])
    }
  }

  return (
    <div ref={pickerRootRef}>
      <DateRangePicker
        className={className}
        value={value}
        minDate={calendarMinDate}
        maxDate={maxDate}
        allowSingleDayRange={true}
        contiguousCalendarMonths={false}
        singleMonthOnly={false}
        selectedShortcutIndex={selectedShortcutIndex}
        shortcuts={translatedShortcuts}
        initialMonth={getInitialMonth(value)}
        onChange={handleChange}
      />
    </div>
  )
}

export default injectIntl(DatePicker)
